import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import { createLogger, formatUnknownError } from '@/core/helpers/logger.helper';
import { getApp, type FirebaseApp } from 'firebase/app';
import { getMessaging, getToken } from 'firebase/messaging';
import type { Router } from 'vue-router';

const logger = createLogger('PushNotifications');

let firebaseApp: FirebaseApp | undefined;

const FIREBASE_API_KEY = import.meta.env.VITE_FIREBASE_API_KEY;
const FIREBASE_AUTH_DOMAIN = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;
const FIREBASE_PROJECT_ID = import.meta.env.VITE_FIREBASE_PROJECT_ID;
const FIREBASE_STORAGE_BUCKET = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;
const FIREBASE_MESSAGING_SENDER_ID = import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID;
const FIREBASE_APP_ID = import.meta.env.VITE_FIREBASE_APP_ID;
const FIREBASE_VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

const firebaseConfigReady = Boolean(
  FIREBASE_API_KEY &&
    FIREBASE_AUTH_DOMAIN &&
    FIREBASE_PROJECT_ID &&
    FIREBASE_STORAGE_BUCKET &&
    FIREBASE_MESSAGING_SENDER_ID &&
    FIREBASE_APP_ID &&
    FIREBASE_VAPID_KEY,
);

export const isFirebasePushConfigured = (): boolean => firebaseConfigReady;

export const registerServiceWorker = async () => {
  if (!navigator.serviceWorker) return;

  return await navigator.serviceWorker.register('/firebase-messaging-sw.js');
};

/**
 * The service worker hands a tapped notification's path to the open app instead of reloading it.
 * Sparks links are the exception: the Sparks page lands on a card it already holds without
 * refetching, so a proposal that has since become a gambit would show stale until a reload.
 */
export const listenForNotificationClicks = (router: Router) => {
  navigator.serviceWorker?.addEventListener('message', (event: MessageEvent) => {
    const { type, path } = event.data ?? {};
    if (type !== 'NOTIFICATION_CLICK' || typeof path !== 'string' || !path.startsWith('/')) return;

    if (router.resolve(path).name === RouterNameType.SPARKS) {
      window.location.assign(path);
      return;
    }

    router.push(path).catch((err) => logger.error(`Notification navigation failed: ${formatUnknownError(err)}`));
  });
};

export const isPushPermissionGranted = () => {
  if (!('Notification' in window)) {
    return false;
  }

  return typeof Notification !== 'undefined' && Notification.permission === 'granted';
};

export const getPushNotificationToken = async (): Promise<string | null> => {
  if (!('Notification' in window)) {
    return null;
  }

  const initialized = await initializePushNotifications();
  if (!initialized || !firebaseApp) {
    return null;
  }

  if (!isPushPermissionGranted()) {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;
  }

  try {
    const messaging = getMessaging(firebaseApp);
    const currentToken = await getToken(messaging, { vapidKey: FIREBASE_VAPID_KEY });

    if (currentToken) {
      logger.info(`Received the token: ${currentToken}`);
    }

    return currentToken;
  } catch (err) {
    logger.error(`Error retrieving FCM token: ${formatUnknownError(err)}`);

    return null;
  }
};

let inFlightInit: Promise<boolean> | null = null;

async function performFirebaseInit(): Promise<boolean> {
  try {
    // Reuse the default Firebase app created for Realtime Database / Firestore (avoids duplicate-app errors).
    await import('@/core/network/firebase');
    await registerServiceWorker();
    firebaseApp = getApp();

    return true;
  } catch (err) {
    logger.error(`Firebase push init failed: ${formatUnknownError(err)}`);

    return false;
  } finally {
    inFlightInit = null;
  }
}

/** Returns whether Firebase Cloud Messaging is ready for token retrieval. */
export const initializePushNotifications = async (): Promise<boolean> => {
  if (!firebaseConfigReady) {
    return false;
  }

  if (firebaseApp) {
    return true;
  }

  if (!inFlightInit) {
    inFlightInit = performFirebaseInit();
  }

  return inFlightInit;
};
